// Editor de Pílula Técnica dentro de Manuais: abre o editor (HTML com blocos) em tela cheia,
// e "Salvar na galeria" gera o PDF que vai para o técnico + guarda a versão editável no manual.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  AppBar, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Radio, RadioGroup, TextField, Toolbar, Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";

import suporteApi, { mensagemErro } from "../api";
import { MARCA } from "../../context/DarkMode";
import { CampoTags } from "./Manuais";

const LIBS = [
  "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js",
  "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js",
];
const FOLHA = { w: 210, h: 297, m: 8 }; // mm: A4, para ler no celular
// formato do PDF (folha A4): largura do layout usado na captura; quanto mais estreito, maior a letra
const FORMATOS = {
  celular: { largura: 440, nome: "Celular — letra grande (recomendado para o WhatsApp)" },
  a4: { largura: 960, nome: "A4 para impressão — layout completo" },
};
// cards que nunca são partidos entre páginas (se não couberem no resto da página, vão inteiros para a próxima)
const EVITAR = ".passo, .box, .card-texto, .card-suporte, .check, .imagem, .visor-zonas, img, figure, table, tr, "
  + "article, aside, p, li, h2, h3, .dupla > *";
const TITULOS = "h2, h3"; // título nunca fica sozinho no pé da página: vai junto com o que vem logo depois

// ------------------------------------------------------------------ paginação (mesma regra na prévia e no PDF)
// Ajusta o corte (px do canvas) para cima até uma faixa "lisa": linha igual às vizinhas = entre linhas de texto,
// fora de imagem. Evita cortar letra se a medição da tela e a imagem diferirem alguns pixels.
const linhaLivre = (canvas, alvo, alcance) => {
  const ctx = canvas.getContext("2d");
  const larg = canvas.width;
  const passo = Math.max(1, Math.floor(larg / 300));
  const ler = (yy) => ctx.getImageData(0, yy, larg, 1).data;
  const igual = (a, b) => {
    let dif = 0;
    for (let i = 0; i < a.length; i += 4 * passo) dif += Math.abs(a[i] - b[i]) + Math.abs(a[i + 1] - b[i + 1]) + Math.abs(a[i + 2] - b[i + 2]);
    return dif / (a.length / (4 * passo)) < 1.5;
  };
  for (let yy = Math.min(alvo, canvas.height - 2); yy > Math.max(2, alvo - alcance); yy -= 1) {
    const meio = ler(yy);
    if (igual(meio, ler(yy - 1)) && igual(meio, ler(yy + 1))) return yy;
  }
  return alvo;
};

// Percorre os blocos e decide as páginas A4. porBloco(bl, r, larg) (opcional, só no PDF) devolve { ajustar(ini, fim) };
// onFatia({ bl, r, ini, fim, pagina, y, mm, dados }) recebe cada pedaço na ordem. Devolve o total de páginas.
const paginar = async (doc, { porBloco, onFatia }) => {
  const largura = FOLHA.w - 2 * FOLHA.m;
  const util = FOLHA.h - 2 * FOLHA.m;
  let pagina = 1;
  let y = FOLHA.m;
  let fundoAnterior = null;
  const novaPagina = () => { pagina += 1; y = FOLHA.m; fundoAnterior = null; };
  for (const bl of Array.from(doc.querySelectorAll("#documento > .bloco"))) {
    if (bl.dataset.tipo === "pagina") { if (y > FOLHA.m) novaPagina(); continue; }
    const r = bl.getBoundingClientRect();
    if (!r.height || !r.width) continue;
    // conteúdo mais largo que o bloco (ex.: tabela/visor fixo): entra inteiro, reduzido para caber
    const larg = Math.max(r.width, bl.scrollWidth);
    const mm = largura / larg; // mm por px de tela
    if (fundoAnterior !== null && y > FOLHA.m) y = Math.min(y + Math.max(0, r.top - fundoAnterior) * mm, FOLHA.h - FOLHA.m);
    const dados = porBloco ? await porBloco(bl, r, larg) : null;
    const pagPx = util / mm;
    const faixa = (e, ate) => {
      const q = e.getBoundingClientRect();
      return [q.top - r.top, (ate || q).bottom - r.top];
    };
    const faixas = [
      ...Array.from(bl.querySelectorAll(EVITAR)).map((e) => faixa(e)),
      ...Array.from(bl.querySelectorAll(TITULOS)).filter((h) => h.nextElementSibling)
        .map((h) => faixa(h, h.nextElementSibling.getBoundingClientRect())),
    ].filter(([t, f]) => f - t < pagPx);
    const cortes = Array.from(new Set(Array.from(bl.querySelectorAll("*")).flatMap((e) => {
      const q = e.getBoundingClientRect();
      return [Math.round(q.top - r.top), Math.round(q.bottom - r.top)];
    }))).filter((c) => c > 0 && c < r.height && !faixas.some(([t, f]) => c > t + 1 && c < f - 1)).sort((p, q) => p - q);
    let ini = 0;
    while (ini < r.height - 1) {
      const cabe = (FOLHA.h - FOLHA.m - y) / mm;
      if (ini === 0 && r.height > cabe && r.height <= pagPx && y > FOLHA.m) { novaPagina(); continue; } // cabe inteiro numa página nova
      let fim = r.height;
      if (r.height - ini > cabe) {
        fim = cortes.filter((c) => c > ini + 20 && c <= ini + cabe).pop();
        if (!fim) {
          if (y > FOLHA.m) { novaPagina(); continue; }
          fim = ini + cabe; // card maior que uma página inteira: corta onde der
        }
        if (dados && dados.ajustar) fim = dados.ajustar(ini, fim);
      }
      await onFatia({ bl, r, ini, fim, pagina, y, mm, dados });
      y += (fim - ini) * mm;
      ini = fim;
      if (ini < r.height - 1) novaPagina();
    }
    fundoAnterior = r.bottom;
  }
  return pagina;
};

// Largura padrão do formato: o conteúdo do editor fica com exatamente `largura` px, com ou sem barra de rolagem
// (a barra não "rouba" largura do layout) — é o que garante que prévia e PDF quebrem nos mesmos lugares.
const fixarLargura = async (quadro, doc, win, largura) => {
  quadro.style.width = `${largura}px`;
  await new Promise((r) => win.requestAnimationFrame(() => win.requestAnimationFrame(r)));
  const barra = quadro.getBoundingClientRect().width - doc.documentElement.clientWidth;
  if (barra > 0) {
    quadro.style.width = `${largura + barra}px`;
    await new Promise((r) => win.requestAnimationFrame(() => win.requestAnimationFrame(r)));
  }
};

const MARCA_PAGINA = "marca-pagina";
const limparMarcas = (doc) => doc.querySelectorAll(`.${MARCA_PAGINA}`).forEach((e) => e.remove());

const mensagemPadrao = (titulo, resumo) =>
  `Pílula Técnica Allarmi - ${titulo}\n\n${resumo}\n\nConfira a Pílula Técnica em anexo.\nUma dose de conhecimento para evoluir a cada atendimento! 💚`;

const semColchetes = (t) => (/^\s*\[.*\]\s*$/.test(t || "") ? "" : (t || "").trim());

// cópia editável do editor (mesma lógica do "Salvar edição" original): estado dos campos, sem o menu lateral
const fonteDoEditor = (doc) => {
  const copia = doc.documentElement.cloneNode(true);
  const originais = doc.querySelectorAll("input");
  copia.querySelectorAll("input").forEach((el, i) => {
    const o = originais[i];
    if (!o) return;
    if (el.type === "checkbox") { if (o.checked) el.setAttribute("checked", ""); else el.removeAttribute("checked"); }
    else if (el.type !== "file") el.setAttribute("value", o.value);
  });
  copia.querySelectorAll("#lateral, [data-injetado], meta[name='allarmi-manual']").forEach((el) => el.remove());
  copia.querySelector("body")?.classList.remove("gerando-pdf");
  const st = copia.querySelector("#status");
  if (st) st.textContent = "Pílula salva na galeria de manuais.";
  return `<!doctype html>\n${copia.outerHTML}`;
};

const EditorPilula = ({ manual, filas, tags: todasTags = [], onClose, onSalvo }) => {
  const iframe = useRef(null);
  const [html, setHtml] = useState(null);
  const [form, setForm] = useState(null); // diálogo "Salvar na galeria"
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (manual?.htmlInicial) { setHtml(manual.htmlInicial); return; } // "Importar pílula": HTML vindo do computador
    const url = manual?.id ? `/api/manuais/${manual.id}/fonte` : "/static/pilula-modelo.html";
    suporteApi.get(url, { responseType: "text", transformResponse: (r) => r })
      .then((r) => setHtml(r.data))
      .catch((e) => { toast.error(mensagemErro(e)); onClose(); });
  }, [manual]); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirSalvar = () => {
    const doc = iframe.current?.contentDocument;
    if (!doc) return;
    const h1 = semColchetes(doc.querySelector("h1")?.innerText);
    const resumo = semColchetes(doc.querySelector(".resumo")?.innerText);
    if (!h1) { toast.warn("Escreva o título da pílula antes de salvar"); return; }
    setForm({
      titulo: manual?.titulo || `PÍLULA – ${h1.toUpperCase()}`,
      mensagem: manual?.mensagem || mensagemPadrao(h1, resumo || "Preparamos um passo a passo com imagens para ajudar você no atendimento."),
      filas: manual?.filas?.length ? manual.filas : [1],
      tags: manual?.tags || [],
      formato: "celular",
    });
  };

  // ao carregar: "Salvar edição" vira "Salvar na galeria" e carrega o gerador de PDF
  const prepararEditor = () => {
    const doc = iframe.current?.contentDocument;
    if (!doc) return;
    previaRef.current = false; // editor recarregado: volta ao modo edição
    setPrevia(false);
    iframe.current.style.width = '100%';
    const b = doc.getElementById("salvar");
    if (b) {
      const novo = b.cloneNode(true);
      novo.textContent = "Salvar na galeria";
      b.replaceWith(novo);
      novo.addEventListener("click", abrirSalvar);
    }
    const ajuda = doc.querySelector(".ajuda");
    if (ajuda) ajuda.textContent = "Monte a página pelo menu lateral. “Salvar na galeria” gera o PDF que vai para o técnico e guarda esta versão para editar depois.";
    if (!doc.querySelector("script[data-injetado]")) {
      LIBS.forEach((src) => {
        const sc = doc.createElement("script");
        sc.src = src;
        sc.dataset.injetado = "1";
        doc.head.appendChild(sc);
      });
      const st = doc.createElement("style");
      st.dataset.injetado = "1";
      st.textContent = ".gerando-pdf .acoes,.gerando-pdf .mini,.gerando-pdf button,.gerando-pdf .aviso-pagina,.gerando-pdf .etiqueta-pagina{display:none!important}"
        + ".gerando-pdf [contenteditable]{outline:none!important}.gerando-pdf .folha{box-shadow:none!important;margin:0!important;width:auto!important;max-width:none!important}"
        + ".gerando-pdf #lateral,.gerando-pdf .barra,.gerando-pdf .ajuda,.gerando-pdf .status{display:none!important}.gerando-pdf body,body.gerando-pdf{padding-left:0!important}"
        // no PDF estreito (celular) o visor de 10 zonas vira 2 linhas de 5; nada fica mais largo que a folha
        + "@media(max-width:650px){.gerando-pdf .visor-zonas{grid-template-columns:repeat(5,1fr)!important;row-gap:14px!important}"
        + ".gerando-pdf .bloco img{max-width:100%!important;height:auto}}";
      doc.head.appendChild(st);
    }
  };

  // ---------------------------------------------------------------- prévia celular: largura do PDF + quebras
  const previaRef = useRef(false);
  const [previa, setPrevia] = useState(false);
  const desenharPaginas = async () => {
    const doc = iframe.current?.contentDocument;
    const win = iframe.current?.contentWindow;
    if (!doc || !win || !previaRef.current) return;
    limparMarcas(doc);
    const marcas = [];
    let anterior = 1;
    const total = await paginar(doc, {
      onFatia: ({ r, ini, pagina }) => {
        if (pagina !== anterior) marcas.push({ topo: r.top + win.scrollY + ini, pagina });
        anterior = pagina;
      },
    });
    marcas.forEach(({ topo, pagina }) => {
      const m = doc.createElement("div");
      m.className = MARCA_PAGINA;
      m.dataset.injetado = "1";
      m.style.cssText = `position:absolute;left:0;right:0;top:${topo - 6}px;height:0;border-top:2px dashed #d32f2f;`
        + "pointer-events:none;z-index:50";
      m.innerHTML = '<span style="position:absolute;right:6px;top:-11px;background:#d32f2f;color:#fff;font:600 11px sans-serif;'
        + `padding:2px 8px;border-radius:10px">página ${pagina} de ${total}</span>`;
      doc.body.appendChild(m);
    });
  };
  const alternarPrevia = async () => {
    const doc = iframe.current?.contentDocument;
    const win = iframe.current?.contentWindow;
    if (!doc || !win) return;
    const liga = !previaRef.current;
    previaRef.current = liga;
    setPrevia(liga);
    if (liga) {
      doc.body.classList.add("gerando-pdf");
      await fixarLargura(iframe.current, doc, win, FORMATOS.celular.largura);
      await desenharPaginas();
    } else {
      limparMarcas(doc);
      doc.body.classList.remove("gerando-pdf");
      iframe.current.style.width = "100%";
    }
  };
  // texto editado na prévia: refaz as quebras
  useEffect(() => {
    if (!previa) return undefined;
    const doc = iframe.current?.contentDocument;
    if (!doc) return undefined;
    let espera;
    const mudou = () => { clearTimeout(espera); espera = setTimeout(desenharPaginas, 600); };
    doc.addEventListener("input", mudou);
    return () => { clearTimeout(espera); doc.removeEventListener("input", mudou); };
  }, [previa]); // eslint-disable-line react-hooks/exhaustive-deps

  // PDF bloco a bloco: cada bloco vira imagem (html2canvas) e entra nas páginas decididas por paginar()
  const gerarPdf = async (doc, win, quadro, larguraLayout) => {
    for (let i = 0; i < 60 && !(win.html2canvas && win.jspdf); i += 1) await new Promise((r) => setTimeout(r, 250));
    if (!(win.html2canvas && win.jspdf)) throw new Error("Não consegui carregar o gerador de PDF. Verifique a internet e tente de novo.");
    limparMarcas(doc);
    doc.body.classList.add("gerando-pdf");
    const larguraAntes = quadro.style.width;
    await fixarLargura(quadro, doc, win, larguraLayout);
    try {
      const pdf = new win.jspdf.jsPDF({ unit: "mm", format: "a4", orientation: "portrait", compress: true });
      const largura = FOLHA.w - 2 * FOLHA.m;
      let paginaPdf = 1;
      await paginar(doc, {
        porBloco: async (bl, r, larg) => {
          const canvas = await win.html2canvas(bl, { scale: Math.min(2.5, 24000 / r.height), backgroundColor: "#ffffff",
            useCORS: true, logging: false, windowWidth: doc.documentElement.clientWidth, width: larg });
          const px = canvas.height / r.height;
          return {
            canvas,
            px,
            ajustar: (ini, fim) => {
              const a = linhaLivre(canvas, Math.round(fim * px), Math.round(40 * px)) / px;
              return a > ini + 20 ? a : fim;
            },
          };
        },
        onFatia: ({ ini, fim, pagina, y, mm, dados }) => {
          while (paginaPdf < pagina) { pdf.addPage(); paginaPdf += 1; }
          const { canvas, px } = dados;
          const sy = Math.round(ini * px);
          const sh = Math.max(1, Math.min(canvas.height - sy, Math.round((fim - ini) * px)));
          const fatia = doc.createElement("canvas");
          fatia.width = canvas.width;
          fatia.height = sh;
          const ctx = fatia.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, fatia.width, sh);
          ctx.drawImage(canvas, 0, sy, canvas.width, sh, 0, 0, canvas.width, sh);
          pdf.addImage(fatia.toDataURL("image/jpeg", 0.9), "JPEG", FOLHA.m, y, largura, (fim - ini) * mm, undefined, "FAST");
        },
      });
      return pdf.output("blob");
    } finally {
      if (previaRef.current) {
        await fixarLargura(quadro, doc, win, FORMATOS.celular.largura);
        await desenharPaginas();
      } else {
        doc.body.classList.remove("gerando-pdf");
        quadro.style.width = larguraAntes;
      }
    }
  };

  const salvar = async () => {
    const doc = iframe.current?.contentDocument;
    const win = iframe.current?.contentWindow;
    if (!doc || !win) return;
    setSalvando(true);
    try {
      const pdf = await gerarPdf(doc, win, iframe.current, FORMATOS[form.formato].largura);
      const corpo = new FormData();
      if (manual?.id) corpo.append("id", manual.id);
      corpo.append("titulo", form.titulo);
      corpo.append("mensagem", form.mensagem);
      corpo.append("filas", JSON.stringify(form.filas));
      corpo.append("tags", JSON.stringify(form.tags));
      const nome = `${form.titulo.replace(/[\\/:*?"<>|]+/g, "-").slice(0, 80)}.pdf`;
      corpo.append("pdf", new File([pdf], nome, { type: "application/pdf" }));
      corpo.append("fonte", new File([fonteDoEditor(doc)], "fonte.html", { type: "text/html" }));
      const { data } = await suporteApi.post("/api/manuais/pilula", corpo);
      toast.success(`Pílula salva na galeria (${Math.round(pdf.size / 1024)} KB de PDF)`);
      setForm(null);
      onSalvo(data);
    } catch (e) {
      toast.error(e?.response ? mensagemErro(e) : e.message);
    }
    setSalvando(false);
  };

  return (
    <Dialog open fullScreen onClose={onClose}>
      <AppBar position="static" style={{ background: MARCA.escuro }}>
        <Toolbar variant="dense">
          <Typography style={{ flex: 1, fontWeight: 600 }}>
            {manual?.id ? `Editar pílula · ${manual.titulo}` : manual?.htmlInicial ? `Importar pílula · ${manual.arquivo}` : "Nova pílula técnica"}
          </Typography>
          {html && (
            <Button style={{ color: "#fff", borderColor: "rgba(255,255,255,.5)", marginRight: 8 }} variant="outlined"
              startIcon={<PhoneAndroidIcon />} onClick={alternarPrevia}>
              {previa ? "Voltar à edição" : "Prévia celular (páginas do PDF)"}
            </Button>
          )}
          <Button style={{ color: "#fff" }} startIcon={<CloseIcon />} onClick={onClose}>Fechar</Button>
        </Toolbar>
      </AppBar>
      {!html ? <div style={{ padding: 24 }}><CircularProgress size={24} /></div> : (
        <iframe ref={iframe} title="Editor de Pílula Técnica" srcDoc={html} onLoad={prepararEditor}
          style={{ border: 0, width: "100%", flex: 1, height: "calc(100vh - 48px)", background: "#edf0eb", margin: "0 auto",
            display: "block", boxShadow: previa ? "0 0 0 1px #c5d1bd, 0 8px 30px rgba(0,0,0,.18)" : "none" }} />
      )}
      {previa && (
        <div style={{ position: "fixed", bottom: 12, left: "50%", transform: "translateX(-50%)", background: MARCA.escuro, color: "#fff",
          padding: "6px 14px", borderRadius: 16, fontSize: 13, zIndex: 1400, maxWidth: "90vw", textAlign: "center" }}>
          Prévia do PDF no formato celular · a linha vermelha mostra onde cada página começa · dá para editar o texto aqui
        </div>
      )}
      <Dialog open={!!form} onClose={() => !salvando && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Salvar na galeria de manuais</DialogTitle>
        {form && (
          <DialogContent>
            <TextField id="pil-titulo" label="Título do manual" fullWidth variant="outlined" size="small" margin="dense"
              value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
            <TextField id="pil-msg" label="Mensagem que vai junto" fullWidth variant="outlined" size="small" margin="dense" multiline rows={6}
              value={form.mensagem} onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
              helperText="Texto padrão das pílulas; aceita {{nome}}, {{os}}… como nas respostas rápidas" />
            <CampoTags valor={form.tags} opcoes={todasTags} onChange={(tags) => setForm({ ...form, tags })} />
            <Typography variant="subtitle2" style={{ marginTop: 10 }}>Formato do PDF</Typography>
            <RadioGroup value={form.formato} onChange={(e) => setForm({ ...form, formato: e.target.value })}>
              {Object.entries(FORMATOS).map(([k, f]) => (
                <FormControlLabel key={k} value={k} label={f.nome} control={<Radio size="small" color="primary" />} />
              ))}
            </RadioGroup>
            <Typography variant="subtitle2" style={{ marginTop: 10 }}>Mostrar só nas filas</Typography>
            {filas.map((q) => (
              <FormControlLabel key={q.id} label={q.nome}
                control={<Checkbox size="small" color="primary" checked={form.filas.includes(q.id)}
                  onChange={(e) => setForm({ ...form, filas: e.target.checked ? [...form.filas, q.id] : form.filas.filter((x) => x !== q.id) })} />} />
            ))}
            <Typography variant="caption" color="textSecondary" component="div">
              Ao salvar, o sistema gera o PDF da pílula (vai para o técnico no WhatsApp) e guarda esta versão para editar depois.
            </Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={salvando}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={salvar} disabled={salvando || !form?.titulo?.trim()}>
            {salvando ? <><CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />Gerando PDF…</> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EditorPilula;
